import styled from '@emotion/styled';
import React from 'react';
import Markdown from 'react-markdown';
import { useDispatch, useSelector } from 'react-redux';
import Collapse, { Panel } from 'rc-collapse';

import Responsive from '../../components/responsive/Responsive';
import NRLCover from 'app/images/rapidFire/cover-nrl.jpg';
import NRLCoverMobile from 'app/images/rapidFire/cover-nrl-mobile.jpg';
import Step1 from 'app/images/rapidFire/Step1.png';
import Step2 from 'app/images/rapidFire/Step2.png';
import Step3 from 'app/images/rapidFire/Step3.png';
import { fetchFaqs } from 'app/actions/contentActions';

const FAQS = [
  {
    question: 'When do Rapidfire contests become available?',
    answer: `Rapidfire contests are created when team lineups are finalised, which varies from sport to sport as detailed below:

**NRL**
Contests are created for every NRL match and become available 1 hour before each game.

**AFL**
Contests are created for every AFL match and become available 1 hour before each game.

**NFL**
Contests are created for a slate of games (i.e. matchups are coordinated across multiple games) for US Thursday night, Sunday, Sunday night and Monday night. Contests become available 1 hour before the opening game of each slate.

**NBA**
Contests are created daily for a slate of games (i.e. matchups are selected across multiple games). Contests become available approximately 30-45 minutes before the opening game of each slate.

**GOLF**
Contests are created for all PGA tour events and become available approximately 10 hours before the start of each tournament.

**MLB**
Contests are created Tuesday - Saturday for a slate of games (i.e. matchups are selected across multiple games). Contests become available approximately 30-45 minutes before the opening game of each slate.

**CRICKET**

**BBL**
Contests are created for every BBL match and become available after the bat flip, approximately 30 minutes before each game.

**ODI**
 Contests are created for every Australian match as well as all World Cup games and become available approximately 4 hours before the first ball.

**Test Matches**
 Contests are created for all major test matches and become available approximately 4 hours before the first ball.

**F1**
Contests are created for all F1 Grand Prix Approx 8 hours before each race.`
  },
  {
    question: 'What happens if there is a tie?',
    answer: `When there is a tied matchup, a revised payout for entries placed may apply. Payouts will be adjusted as follows:

**RAPIDFIRE 3**
		
- 1 Tied Matchup, 2 Matchup winners - Total stake will be refunded
- 2 Tied Matchups, 1-2 Matchup winners - Total stake will be refunded
- 3 Tied Matchups - Total stake will be refunded.

**RAPIDFIRE 5**

- 1 Tied Matchup, 4 Matchup Winners, Player will receive 50% of base payout
- 2 Tied Matchups, 3 Matchup winners, Player will receive 25% of base payout
- 3-5 Tied Matchups, Total stake will be refunded.`
  },
  {
    question: "What happens if one of my players doesn't play?",
    answer: `Where one of the players in a tier plays no game time in a match (either due to non-selection, being an unused substitute, or the match being cancelled) then the result of that tier will be considered a tied tier for calculating payouts. Payouts may be adjusted in the manner described above. For AFL if a player in Rapidfire is the named as the starting substitute, the tier will be considered a tied tier.`
  }
];

const RapidFireLanding = ({ showHeader = false, hasCompetition }) => {
  const [activeKey, setActiveKey] = React.useState(null);
  const handleChange = activeKey => {
    setActiveKey(activeKey);
  };

  const faqs = useSelector(state => state.faqs && state.faqs.data);
  const dispatch = useDispatch();

  const scoringsFAQS = React.useMemo(() => {
    if (Array.isArray(faqs)) {
      const result = faqs.find(item => item.id === 0);
      return result && result.items ? result.items : [];
    } else {
      return [];
    }
  }, [faqs]);

  React.useEffect(() => {
    dispatch(fetchFaqs());
  }, [dispatch]);

  const handleScrollToElement = element => () => {
    const offset = 100;
    const bodyRect = document.body.getBoundingClientRect().top;
    const elementRect = element.getBoundingClientRect().top;
    const elementPosition = elementRect - bodyRect;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  };

  const whatsRapidfireRef = React.useRef(null);
  const howToPlayRef = React.useRef(null);
  const faqRef = React.useRef(null);
  const scoringRef = React.useRef(null);

  return (
    <Responsive desktop>
      {isDesktop => (
        <Stack>
          {showHeader && <HeaderImage src={isDesktop ? NRLCover : NRLCoverMobile} />}
          {!hasCompetition && (
            <Text
              style={{
                marginTop: '0, 40px'
              }}
            >
              There are no Rapidfire contests available at this time.
              <br />
              Check back again soon.
            </Text>
          )}
          <Title>CONSIDER YOURSELF AN ARMCHAIR EXPERT?</Title>
          <SubTitle>Test your skills and get into the game with Draftstars RAPIDFIRE</SubTitle>
          <HStack>
            <ScrollButton
              isDesktop={isDesktop}
              onClick={handleScrollToElement(whatsRapidfireRef.current)}
            >
              WHAT IS RAPIDFIRE?
            </ScrollButton>
            <ScrollButton
              isDesktop={isDesktop}
              onClick={handleScrollToElement(howToPlayRef.current)}
            >
              HOW TO PLAY
            </ScrollButton>
            <ScrollButton isDesktop={isDesktop} onClick={handleScrollToElement(faqRef.current)}>
              FAQS
            </ScrollButton>
            {scoringsFAQS && scoringsFAQS.length > 0 && (
              <ScrollButton
                isDesktop={isDesktop}
                onClick={handleScrollToElement(scoringRef.current)}
              >
                SCORING
              </ScrollButton>
            )}
          </HStack>
          <TitleWithUnderline ref={whatsRapidfireRef}>WHAT IS RAPIDFIRE?</TitleWithUnderline>
          <Text>
            Rapidfire is the easiest way to play daily fantasy sports. To enter, simply pick who you
            think will score more Draftstars points for each of the five chosen head-to-head
            match-ups. There are two Rapidfire game types to choose from:
          </Text>
          <BoldText>
            Enter RAPIDFIRE 3 and pick at least 3 out of 5 matchups for a 1.8x return{' '}
          </BoldText>
          <Text>OR</Text>
          <BoldText>Enter RAPIDFIRE 5 and pick all 5 for a massive 20x return</BoldText>
          <TitleWithUnderline ref={howToPlayRef}>HOW TO PLAY RAPIDFIRE?</TitleWithUnderline>
          <div
            style={{
              display: 'flex',
              flexDirection: isDesktop ? 'row' : 'column',
              justifyContent: 'space-around',
              alignItems: 'center'
            }}
          >
            <Step title="STEP 1" description="Choose your contest" image={Step1} />
            <Step title="STEP 2" description="Select your players" image={Step2} />
            <Step title="STEP 3" description="Watch & win" image={Step3} />
          </div>
          <TitleWithUnderline ref={faqRef}>FAQS</TitleWithUnderline>
          <div
            style={{
              textAlign: 'left'
            }}
          >
            <Collapse
              accordion
              activeKey={activeKey}
              onChange={handleChange}
              prefixCls="faq-collapse"
            >
              {FAQS.map((item, index) => (
                <Panel key={`faq_${index.toString()}`} header={item.question}>
                  <Markdown source={item.answer} />
                </Panel>
              ))}
            </Collapse>
          </div>
          {scoringsFAQS && scoringsFAQS.length > 0 && (
            <div>
              <TitleWithUnderline ref={scoringRef}>SCORING</TitleWithUnderline>
              <Text
                style={{
                  marginBottom: 30
                }}
              >
                Players accumulate points in the following ways:
              </Text>
              <div
                style={{
                  textAlign: 'left'
                }}
              >
                <Collapse
                  accordion
                  activeKey={activeKey}
                  onChange={handleChange}
                  prefixCls="faq-collapse"
                >
                  {scoringsFAQS.map((item, idx) => (
                    <Panel key={`scoring_${idx.toString()}`} header={item.title}>
                      <Markdown source={item.content} />
                    </Panel>
                  ))}
                </Collapse>
              </div>
            </div>
          )}
          <ScrollButton
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            style={{
              margin: '40px auto'
            }}
          >
            GO TO TOP
          </ScrollButton>
        </Stack>
      )}
    </Responsive>
  );
};

const Step = ({ title, description, image }) => (
  <VStack style={{ justifyContent: 'center', alignItems: 'center', marginBottom: 20 }}>
    <Text
      style={{
        fontFamily: 'Barlow Condensed',
        color: '#FF0056',
        fontSize: 30,
        fontStyle: 'italic',
        fontWeight: '600'
      }}
    >
      {title}
    </Text>

    <HeaderImage
      style={{
        maxWidth: 138,
        maxHeight: 138
      }}
      src={image}
    />
    <Text style={{ color: 'white', fontSize: 18, marginTop: 16 }}>{description}</Text>
  </VStack>
);

const TitleWithUnderline = styled.div({
  fontSize: 28,
  fontWeight: 300,
  color: 'white',
  paddingBottom: 10,
  textDecorationLine: 'underline',
  textDecorationColor: '#FF0056',
  marginBottom: 30,
  marginTop: 55
});

const VStack = styled.div({
  justifyContent: 'center',
  alignItems: 'stretch',
  marginBottom: 55,
  display: 'flex',
  flexDirection: 'column'
});

const ScrollButton = styled.button({
  width: 200,
  fontSize: 18,
  backgroundColor: '#FF0056',
  padding: 10,
  flexShrink: 0,
  flexGrow: 1
});

const HStack = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-around',
  alignItems: 'center',
  marginBottom: theme.spacing.small,
  flexWrap: 'wrap',
  gap: 10
}));

const Title = styled.div(({ theme }) => ({
  fontFamily: 'Barlow Condensed',
  fontSize: 60,
  fontStyle: 'italic',
  fontWeight: '500',
  lineHeight: '1.2em',
  marginTop: 40,
  marginBottom: theme.spacing.small,
  color: '#FF0056'
}));

const SubTitle = styled.div({
  fontSize: 28,
  color: 'white',
  fontWeight: 200,
  marginBottom: 30
});

const Stack = styled.div(
  {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    fontFamily: 'Barlow'
  },
  ({ theme }) => ({
    fontSize: theme.fontSize.medium
  })
);
const HeaderImage = styled.img({
  width: '100%'
});

const Text = styled.div({
  fontFamily: 'Barlow',
  color: '#fff',
  fontSize: 18,
  fontWeight: 400
});

const BoldText = styled(Text)({
  fontSize: 24,
  fontWeight: 'bold',
  margin: '10px 0'
});

export default RapidFireLanding;
